import * as _ from "lodash";

/**
 * Static string of valid Base64 chars.
 */
const BASE64_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";

export class StringEscapeUtilsBase {
	static escapeBase64(source: string | undefined): string | undefined {
		const input = StringEscapeUtilsBase.encodeUtf8ToByteString(source);

		if (!input) {
			return undefined;
		}

		let result = "";
		let charsLeft = 0;
		let char1: number;
		let char2: number;
		let char3: number;
		let byte1: number;
		let byte2: number;
		let byte3: number;
		let byte4: number;
		for (let i = 0; i < input.length; i++) {
			charsLeft = input.length - i;
			char1 = input.charAt(i).charCodeAt(0);
			byte1 = char1 >> 2;
			if (charsLeft >= 3) {
				char2 = input.charAt((i += 1)).charCodeAt(0);
				char3 = input.charAt((i += 1)).charCodeAt(0);
				byte2 = ((char1 & 3) << 4) | (char2 >> 4);
				byte3 = ((char2 & 15) << 2) | (char3 >> 6);
				byte4 = char3 & 63;
			} else if (charsLeft === 2) {
				char2 = input.charAt((i += 1)).charCodeAt(0);
				char3 = 0;
				byte2 = ((char1 & 3) << 4) | (char2 >> 4);
				byte3 = ((char2 & 15) << 2) | (char3 >> 6);
				byte4 = 64;
			} else {
				char2 = 0;
				byte2 = ((char1 & 3) << 4) | (char2 >> 4);
				byte3 = byte4 = 64;
			}
			result = result.concat(BASE64_CHARS.charAt(byte1));
			result = result.concat(BASE64_CHARS.charAt(byte2));
			result = result.concat(BASE64_CHARS.charAt(byte3));
			result = result.concat(BASE64_CHARS.charAt(byte4));
		}
		return result;
	}

	static unescapeBase64(source: string | undefined): string | undefined {
		if (!source) {
			return undefined;
		}

		let result = "";
		let charsLeft = 0;
		let char1: number;
		let char2: number;
		let char3: number;
		let byte1: number;
		let byte2: number;
		let byte3: number;
		let byte4: number;
		for (let i = 0; i < source.length; i++) {
			charsLeft = source.length - i;
			byte1 = BASE64_CHARS.indexOf(source.charAt(i));
			if (charsLeft >= 4) {
				byte2 = BASE64_CHARS.indexOf(source.charAt((i += 1)));
				byte3 = BASE64_CHARS.indexOf(source.charAt((i += 1)));
				byte4 = BASE64_CHARS.indexOf(source.charAt((i += 1)));
			} else if (charsLeft === 3) {
				byte2 = BASE64_CHARS.indexOf(source.charAt((i += 1)));
				byte3 = BASE64_CHARS.indexOf(source.charAt((i += 1)));
				byte4 = 0;
			} else if (charsLeft === 2) {
				byte2 = BASE64_CHARS.indexOf(source.charAt((i += 1)));
				byte3 = byte4 = 0;
			} else {
				byte2 = byte3 = byte4 = 0;
			}
			char1 = (byte1 << 2) | (byte2 >> 4);
			char2 = ((byte2 & 15) << 4) | (byte3 >> 2);
			char3 = ((byte3 & 3) << 6) | byte4;
			result = result.concat(String.fromCharCode(char1));
			if (byte3 < 64) {
				result = result.concat(String.fromCharCode(char2));
				if (byte4 < 64) {
					result = result.concat(String.fromCharCode(char3));
				}
			}
		}
		return StringEscapeUtilsBase.decodeUtf8FromByteString(result);
	}

	private static encodeUtf8ToByteString(source: string | undefined): string | undefined {
		if (!source) {
			return undefined;
		}

		let result = "";

		for (let i = 0; i < source.length; i++) {
			const charI: number = source.charAt(i).charCodeAt(0);
			if (charI <= 127) {
				result = result.concat(String.fromCharCode(charI));
			} else if (charI <= 2047) {
				result = result.concat(String.fromCharCode((charI >> 6) | 192));
				result = result.concat(String.fromCharCode((charI & 63) | 128));
			} else if (charI <= 65535) {
				result = result.concat(String.fromCharCode((charI >> 12) | 224));
				result = result.concat(String.fromCharCode(((charI >> 6) & 63) | 128));
				result = result.concat(String.fromCharCode((charI & 63) | 128));
			} else {
				result = result.concat(String.fromCharCode((charI >> 18) | 240));
				result = result.concat(String.fromCharCode(((charI >> 12) & 63) | 128));
				result = result.concat(String.fromCharCode(((charI >> 6) & 63) | 128));
				result = result.concat(String.fromCharCode((charI & 63) | 128));
			}
		}
		return result;
	}

	private static decodeUtf8FromByteString(source: string | undefined): string | undefined {
		if (!source) {
			return undefined;
		}

		let result = "";
		try {
			let byte1: number;
			let byte2: number;
			let byte3: number;
			let byte4: number;
			let out = 0;
			for (let i = 0; i < source.length; i++) {
				byte1 = source.charAt(i).charCodeAt(0);
				if ((byte1 & 240) === 240) {
					byte2 = source.charAt((i += 1)).charCodeAt(0);
					byte3 = source.charAt((i += 1)).charCodeAt(0);
					byte4 = source.charAt((i += 1)).charCodeAt(0);
					byte1 = (byte1 & 7) << 18;
					byte2 = (byte2 & 63) << 12;
					byte3 = (byte3 & 63) << 6;
					byte4 = byte4 & 63;
					out = byte1 | byte2 | byte3 | byte4;
				} else if ((byte1 & 224) === 224) {
					byte2 = source.charAt((i += 1)).charCodeAt(0);
					byte3 = source.charAt((i += 1)).charCodeAt(0);
					byte1 = (byte1 & 15) << 12;
					byte2 = (byte2 & 63) << 6;
					byte3 = byte3 & 63;
					out = byte1 | byte2 | byte3;
				} else if ((byte1 & 192) === 192) {
					byte2 = source.charAt((i += 1)).charCodeAt(0);
					byte1 = (byte1 & 31) << 6;
					byte2 = byte2 & 63;
					out = byte1 | byte2;
				} else {
					out = byte1;
				}
				result = result.concat(String.fromCharCode(out));
			}
		} catch (e: unknown) {
			return "";
		}
		return result;
	}

	static escapeHtml(source: string | undefined): string | undefined {
		return _.escape(source);
	}

	/**
	 * @return A string with all reserved JavaScript characters escaped.
	 */
	static escapeJavaScript(source: string | undefined): string | undefined {
		if (!source) {
			return source;
		}
		return source.replace(new RegExp("([\\\\\"'])", "g"), "\\\\$1");
	}

	static unescapeHtml(source: string | undefined): string | undefined {
		if (source === undefined) {
			return undefined;
		}
		const doc = new DOMParser().parseFromString(source, "text/html");
		return doc.documentElement.textContent ?? undefined;
	}
}
